import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { fetchConsentStatus } from './consentApi';
import { ReceiptAlternativeFlow } from './ReceiptAlternativeFlow';
import { looksLikeFiscalQr } from './scannerFiscalQr';
import type { WasmQrReader } from './scannerWasmReader';
import { isReceiptAlreadyUsedCode, RECEIPT_ALREADY_USED_TEXT, ReceiptRejectionNotice } from './ReceiptRejectionNotice';
import { ConsentScreen } from './screens/ConsentScreen';
import { Icon } from './ui/Icons';
import { HopperIcon } from './ui/icons/HopperIcon';
import './scanner.css';
import './scanner-polish.css';
import './scanner-field-reliability.css';

type DuplicateReceiptSummary = {
  total_amount: string | null;
  currency: string | null;
  merchant_name: string | null;
};

type ScannerStage =
  | 'camera'
  | 'alternatives'
  | 'consent'
  | 'auth-required'
  | 'receipt-auth-required'
  | 'submitting'
  | 'processing'
  | 'polling-error'
  | 'processing-delayed'
  | 'already-submitted'
  | 'candidate'
  | 'success'
  | 'historical'
  | 'merchant-review'
  | 'merchant-ineligible'
  | 'review'
  | 'rejected'
  | 'error';

type CameraState = 'starting' | 'live' | 'manual-only' | 'denied' | 'unavailable';

type Candidate = {
  id: string;
  name: string | null;
  address: string | null;
  status: string;
};

type OfferActivationHint = {
  id?: string;
  status?: string;
} | null;

type ScanReceipt = {
  id: string;
  total_amount: string | null;
  currency: string | null;
  purchase_at: string | null;
  purchase_local_date: string | null;
  visit_id: string | null;
  venue: {
    id: string;
    name: string | null;
    address: string | null;
    timezone: string | null;
  } | null;
};

type ScanPayload = {
  id: string;
  status: string;
  outcome: string | null;
  failure_code: string | null;
  receipt: ScanReceipt | null;
  venue_resolution: {
    case_id: string;
    status: string;
    candidates: Candidate[];
  } | null;
  offer_activated?: boolean;
  offer_activation?: OfferActivationHint;
};

type ScanStatusResponse = {
  scan: ScanPayload;
  offer_activated?: boolean;
  offer_activation?: OfferActivationHint;
};

type ScanStatusReadResult = {
  unauthorized: boolean;
  scan: ScanPayload | null;
};

type SessionResponse = {
  authenticated: boolean;
};

type CsrfResponse = {
  csrf_token: string;
};

type XpResponse = {
  xp?: {
    summary?: {
      total_xp?: number;
    };
  };
};

type BarcodeResult = {
  rawValue?: string;
};

type BarcodeDetectorInstance = {
  detect(source: HTMLVideoElement): Promise<BarcodeResult[]>;
};

type BarcodeDetectorConstructor = {
  new (options?: { formats?: string[] }): BarcodeDetectorInstance;
  getSupportedFormats?: () => Promise<string[]>;
};

type NumericMediaCapability = {
  min: number;
  max: number;
  step?: number;
};

type ExtendedMediaTrackCapabilities = MediaTrackCapabilities & {
  zoom?: NumericMediaCapability;
  torch?: boolean;
  focusMode?: string[];
};

type ExtendedMediaTrackSettings = MediaTrackSettings & {
  zoom?: number;
};

type ExtendedMediaTrackConstraintSet = MediaTrackConstraintSet & {
  zoom?: number;
  torch?: boolean;
  focusMode?: string;
};

type CameraZoom = {
  min: number;
  max: number;
  step: number;
  value: number;
};

type FiscalPreview = {
  amount: string | null;
  date: string | null;
};

type ScannerLauncherProps = {
  onCompleted?: (scan: ScanPayload) => void;
};

const TERMINAL_STATUSES = new Set([
  'confirmed',
  'historical',
  'merchant_review_required',
  'merchant_ineligible',
  'manual_review',
  'rejected',
]);
const PENDING_SCAN_KEY = 'hopper.pendingFiscalQr';
const AUTH_RETURN_TARGET_KEY = 'hopper.authReturnTarget';
const PROFILE_FLASH_KEY = 'hopper.profileFlash';

function getBarcodeDetector(): BarcodeDetectorConstructor | undefined {
  return (window as Window & { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
}

function normalizeQrPayload(raw: string): string {
  return raw.trim().replace(/^\?/, '');
}

function parseFiscalPreview(payload: string): FiscalPreview {
  const params = new URLSearchParams(normalizeQrPayload(payload));
  const rawDate = params.get('t');
  const amount = params.get('s');

  let date: string | null = null;

  if (rawDate && /^\d{8}T\d{4}$/.test(rawDate)) {
    const year = rawDate.slice(0, 4);
    const month = rawDate.slice(4, 6);
    const day = rawDate.slice(6, 8);
    const hour = rawDate.slice(9, 11);
    const minute = rawDate.slice(11, 13);
    date = `${day}.${month}.${year}, ${hour}:${minute}`;
  }

  return { amount, date };
}

function formatMoney(amount: string | null, currency: string | null = 'RUB'): string {
  if (!amount) {
    return 'Сумма уточняется';
  }

  const numeric = Number(amount);

  if (!Number.isFinite(numeric)) {
    return `${amount} ₽`;
  }

  if ((currency ?? 'RUB').toUpperCase() !== 'RUB') {
    return `${numeric.toLocaleString('ru-RU')} ${currency}`;
  }

  return `${numeric.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
}

async function fetchBounded(input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = 12000): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

async function readSession(): Promise<boolean> {
  const response = await fetchBounded('/api/v1/auth/session', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    return false;
  }

  const body = (await response.json()) as SessionResponse;
  return body.authenticated === true;
}

async function readCsrfToken(): Promise<string> {
  const response = await fetchBounded('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`CSRF failed: ${response.status}`);
  }

  const body = (await response.json()) as CsrfResponse;

  if (!body.csrf_token) {
    throw new Error('CSRF token is missing');
  }

  return body.csrf_token;
}

async function readTotalXp(): Promise<number | null> {
  const response = await fetchBounded('/api/v1/me/xp', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    return null;
  }

  const body = (await response.json()) as XpResponse;
  const value = body.xp?.summary?.total_xp;
  return typeof value === 'number' ? value : null;
}

function scanFromStatusResponse(body: ScanStatusResponse): ScanPayload {
  const activation = body.scan.offer_activation ?? body.offer_activation ?? null;
  const activatedByHint = activation?.status === 'activated';

  return {
    ...body.scan,
    offer_activated: body.scan.offer_activated ?? body.offer_activated ?? activatedByHint,
    offer_activation: activation,
  };
}

async function readScanStatus(scanId: string): Promise<ScanStatusReadResult> {
  const response = await fetchBounded(`/api/v1/receipt-scans/${scanId}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (response.status === 401) {
    return { unauthorized: true, scan: null };
  }

  if (!response.ok) {
    throw new Error(`Scan polling failed: ${response.status}`);
  }

  const body = (await response.json()) as ScanStatusResponse;
  return { unauthorized: false, scan: scanFromStatusResponse(body) };
}

function scanActivatedOffer(scan: ScanPayload | null): boolean {
  return scan?.offer_activated === true || scan?.offer_activation?.status === 'activated';
}

function processingLabel(scan: ScanPayload | null): string {
  switch (scan?.status) {
    case 'provider_pending':
      return 'Проверяем фискальный чек';
    case 'venue_resolution_pending':
      return 'Определяем заведение';
    case 'fraud_pending':
      return 'Проверяем визит';
    case 'processing':
      return 'Обрабатываем чек';
    default:
      return 'Подтверждаем визит';
  }
}

function authErrorMessage(code: string | null): string | null {
  switch (code) {
    case 'yandex_phone_required':
      return 'Для аккаунта Хоппер нужен подтверждённый номер телефона. Разрешите Яндекс ID передать номер и попробуйте снова.';
    case 'yandex_denied':
      return 'Вход через Яндекс был отменён. Чек сохранён в этом браузере — можно попробовать ещё раз.';
    case 'yandex_state':
    case 'yandex_code':
      return 'Не удалось безопасно завершить вход через Яндекс. Попробуйте войти ещё раз.';
    case 'account_unavailable':
      return 'Этот аккаунт Хоппер сейчас недоступен.';
    default:
      return code ? 'Не удалось завершить вход через Яндекс. Попробуйте ещё раз.' : null;
  }
}

export function ScannerLauncher({ onCompleted }: ScannerLauncherProps = {}) {
  const [open, setOpen] = useState(false);
  const [mobileLayout, setMobileLayout] = useState(() => window.matchMedia('(max-width: 1119px)').matches);
  const [stage, setStage] = useState<ScannerStage>('camera');
  const [cameraState, setCameraState] = useState<CameraState>('starting');
  const [cameraZoom, setCameraZoom] = useState<CameraZoom | null>(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [manualPayload, setManualPayload] = useState('');
  const [qrPayload, setQrPayload] = useState('');
  const [scan, setScan] = useState<ScanPayload | null>(null);
  const [duplicateReceipt, setDuplicateReceipt] = useState<DuplicateReceiptSummary | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [xpDelta, setXpDelta] = useState<number | null>(null);
  const [pollingScanId, setPollingScanId] = useState<string | null>(null);
  const [photoHint, setPhotoHint] = useState<string | null>(null);
  const [photoReading, setPhotoReading] = useState(false);
  const [previewAvailability, setPreviewAvailability] = useState<'loading' | 'enabled' | 'disabled' | 'unavailable'>('loading');
  const [previewAuthState, setPreviewAuthState] = useState<'checking' | 'signed_in' | 'signed_out' | 'unavailable'>('checking');
  const [alternativeMode, setAlternativeMode] = useState<'photo' | 'manual'>('photo');
  const [alternativePhoto, setAlternativePhoto] = useState<File | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorTimerRef = useRef<number | null>(null);
  const wasmReaderRef = useRef<WasmQrReader | null>(null);
  const qrClaimedRef = useRef(false);
  const cancelledRef = useRef(false);
  const baselineXpRef = useRef<number | null>(null);
  const cameraSettingsRef = useRef<ExtendedMediaTrackSettings | null>(null);

  const stopCamera = useCallback(() => {
    wasmReaderRef.current?.close();
    wasmReaderRef.current = null;
    if (detectorTimerRef.current !== null) {
      window.clearTimeout(detectorTimerRef.current);
      detectorTimerRef.current = null;
    }

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    cameraSettingsRef.current = null;
    setCameraZoom(null);
    setTorchSupported(false);
    setTorchEnabled(false);

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const closeScanner = useCallback(() => {
    cancelledRef.current = true;
    stopCamera();
    sessionStorage.removeItem(PENDING_SCAN_KEY);
    setOpen(false);
    setPhotoHint(null);
  }, [stopCamera]);

  const navigateAndFocus = useCallback((path: string, targetId: string) => {
    closeScanner();

    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentPath !== path) {
      window.history.pushState({}, '', path);
    }

    window.dispatchEvent(new PopStateEvent('popstate'));

    let attempts = 0;
    const focusTarget = () => {
      const target = document.getElementById(targetId);

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        return;
      }

      attempts += 1;
      if (attempts < 120) {
        window.setTimeout(focusTarget, 50);
      }
    };

    window.setTimeout(focusTarget, 50);
  }, [closeScanner]);

  const finishScan = useCallback(async (nextScan: ScanPayload) => {
    setScan(nextScan);
    setDuplicateReceipt(null);

    if (nextScan.status === 'confirmed' || nextScan.status === 'historical') {
      if (nextScan.outcome === 'already_submitted') {
        setXpDelta(null);
        setPollingScanId(null);
        setStage('already-submitted');
        return true;
      }

      let finalXp: number | null = null;
      try {
        finalXp = await readTotalXp();
      } catch {
        // A bounded optional XP balance read cannot conceal confirmed receipt success.
      }
      const baseline = baselineXpRef.current;
      setXpDelta(finalXp !== null && baseline !== null ? Math.max(0, finalXp - baseline) : null);
      setPollingScanId(null);
      setStage(nextScan.status === 'historical' ? 'historical' : 'success');
      onCompleted?.(nextScan);
      return true;
    }

    if (nextScan.status === 'processing_delayed') {
      setPollingScanId(nextScan.id);
      setStage('processing-delayed');
      return true;
    }

    if (nextScan.status === 'merchant_review_required') {
      setPollingScanId(null);
      setStage('merchant-review');
      return true;
    }

    if (nextScan.status === 'merchant_ineligible') {
      setPollingScanId(null);
      setStage('merchant-ineligible');
      return true;
    }

    if (nextScan.status === 'manual_review') {
      setPollingScanId(null);
      setStage('review');
      return true;
    }

    if (nextScan.status === 'rejected') {
      setPollingScanId(null);
      setStage(isReceiptAlreadyUsedCode(nextScan.failure_code ?? '') ? 'already-submitted' : 'rejected');
      return true;
    }

    const candidates = nextScan.venue_resolution?.candidates ?? [];
    const selectable = candidates.filter((candidate) => candidate.status !== 'rejected');

    if (nextScan.status === 'venue_resolution_pending' && selectable.length > 0) {
      setPollingScanId(null);
      setStage('candidate');
      return true;
    }

    if (nextScan.outcome === 'already_submitted') {
      return false;
    }

    return TERMINAL_STATUSES.has(nextScan.status);
  }, [onCompleted]);

  const handlePollingFailure = useCallback((scanId: string, text: string) => {
    setPollingScanId(scanId);
    setMessage(text);
    setStage('polling-error');
  }, []);

  const pollScan = useCallback(async (scanId: string) => {
    setPollingScanId(scanId);
    setMessage(null);
    setStage('processing');

    for (let attempt = 0; attempt < 90; attempt += 1) {
      if (cancelledRef.current) {
        return;
      }

      try {
        const result = await readScanStatus(scanId);

        if (result.unauthorized) {
          if (qrPayload) {
            sessionStorage.setItem(PENDING_SCAN_KEY, qrPayload);
          }
          setStage('auth-required');
          return;
        }

        if (result.scan) {
          const done = await finishScan(result.scan);

          if (done) {
            return;
          }
        }
      } catch {
        handlePollingFailure(
          scanId,
          'Не удалось получить свежий статус чека. Проверьте соединение и запросите статус вручную.',
        );
        return;
      }

      await new Promise((resolve) => window.setTimeout(resolve, 1200));
    }

    setPollingScanId(scanId);
    setMessage('Чек сохранён. Вы можете проверить результат позже.');
    setStage('processing-delayed');
  }, [finishScan, handlePollingFailure, qrPayload]);

  const retryPollingStatus = useCallback(async () => {
    if (!pollingScanId) {
      return;
    }

    setMessage(null);
    setStage('processing');

    try {
      const result = await readScanStatus(pollingScanId);

      if (result.unauthorized) {
        if (qrPayload) {
          sessionStorage.setItem(PENDING_SCAN_KEY, qrPayload);
        }
        setStage('auth-required');
        return;
      }

      if (!result.scan) {
        throw new Error('Scan status is missing');
      }

      const done = await finishScan(result.scan);
      if (!done) {
        setMessage('Чек ещё обрабатывается. Можно проверить результат позже.');
        setStage('processing-delayed');
      }
    } catch {
      setMessage('Статус пока не удалось получить. Проверьте соединение и попробуйте снова.');
      setStage('polling-error');
    }
  }, [finishScan, pollingScanId, qrPayload]);

  const submitPayload = useCallback(async (payload: string) => {
    const normalized = normalizeQrPayload(payload);

    if (!looksLikeFiscalQr(normalized)) {
      setMessage('Это не похоже на QR фискального чека. Проверьте строку и попробуйте ещё раз.');
      setStage('error');
      return;
    }

    setQrPayload(normalized);
    setMessage(null);
    setStage('submitting');

    const authenticated = await readSession();

    if (!authenticated) {
      sessionStorage.setItem(PENDING_SCAN_KEY, normalized);
      setStage('auth-required');
      return;
    }

    const consentStatus = await fetchConsentStatus();

    if (!consentStatus.data.has_consent) {
      sessionStorage.setItem(PENDING_SCAN_KEY, normalized);
      setStage('consent');
      return;
    }

    baselineXpRef.current = await readTotalXp();
    const csrf = await readCsrfToken();

    const response = await fetchBounded('/api/v1/receipt-scans', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrf,
      },
      body: JSON.stringify({ qr_payload: normalized }),
    });

    if (response.status === 401) {
      sessionStorage.setItem(PENDING_SCAN_KEY, normalized);
      setStage('auth-required');
      return;
    }

    const body = await response.json() as {
      scan?: { id?: string; status?: string };
      error?: { code?: string; reason?: string } | string;
      code?: string;
    };
    const nestedErrorCode = typeof body.error === 'object' ? body.error?.code : undefined;

    if (response.status === 403 && (body.code === 'consent_required' || nestedErrorCode === 'consent_required')) {
      sessionStorage.setItem(PENDING_SCAN_KEY, normalized);
      setStage('consent');
      return;
    }

    if (!response.ok) {
      if (response.status === 409 || response.status === 422) {
        sessionStorage.removeItem(PENDING_SCAN_KEY);
        if (isReceiptAlreadyUsedCode(nestedErrorCode ?? body.code ?? '')) {
          setDuplicateReceipt(null);
          setScan(null);
          setMessage(null);
          setStage('already-submitted');
          return;
        }
        setMessage('Не удалось подтвердить фискальный QR. Проверьте чек и попробуйте снова.');
        setStage('rejected');
        return;
      }

      throw new Error(`Receipt submit failed: ${response.status}`);
    }

    const scanId = body.scan?.id;

    if (!scanId) {
      throw new Error('Receipt scan id is missing');
    }

    sessionStorage.removeItem(PENDING_SCAN_KEY);
    await pollScan(scanId);
  }, [pollScan]);

  const acceptPayload = useCallback(async (payload: string) => {
    stopCamera();

    try {
      await submitPayload(payload);
    } catch {
      setMessage('Не удалось обработать чек. Попробуйте ещё раз.');
      setStage('error');
    }
  }, [stopCamera, submitPayload]);

  const applyZoom = useCallback(async (requested: number) => {
    const videoTrack = streamRef.current?.getVideoTracks()[0];
    const zoom = cameraZoom;

    if (!videoTrack || !zoom) {
      return;
    }

    const next = Math.min(zoom.max, Math.max(zoom.min, requested));
    const constraint: ExtendedMediaTrackConstraintSet = { zoom: next };

    try {
      await videoTrack.applyConstraints({ advanced: [constraint] });
      const settings = videoTrack.getSettings() as ExtendedMediaTrackSettings;
      cameraSettingsRef.current = settings;
      setCameraZoom((current) => current === null ? null : {
        ...current,
        value: typeof settings.zoom === 'number' ? settings.zoom : next,
      });
    } catch {
      // Capability reporting differs between browsers; keep the current zoom if application is rejected.
    }
  }, [cameraZoom]);

  const toggleTorch = useCallback(async () => {
    const videoTrack = streamRef.current?.getVideoTracks()[0];

    if (!videoTrack || !torchSupported) {
      return;
    }

    const next = !torchEnabled;
    const constraint: ExtendedMediaTrackConstraintSet = { torch: next };

    try {
      await videoTrack.applyConstraints({ advanced: [constraint] });
      setTorchEnabled(next);
    } catch {
      if (next) {
        setTorchEnabled(false);
      }
    }
  }, [torchEnabled, torchSupported]);

  const startCamera = useCallback(async () => {
    stopCamera();
    setCameraState('starting');
    qrClaimedRef.current = false;
    setStage('camera');
    setMessage(null);
    cancelledRef.current = false;

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraState('unavailable');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      if (cancelledRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      const videoTrack = stream.getVideoTracks()[0];

      if (videoTrack) {
        const initialSettings = videoTrack.getSettings() as ExtendedMediaTrackSettings;
        cameraSettingsRef.current = initialSettings;

        if (typeof videoTrack.getCapabilities === 'function') {
          try {
            const capabilities = videoTrack.getCapabilities() as ExtendedMediaTrackCapabilities;

            if (capabilities.focusMode?.includes('continuous')) {
              const focusConstraint: ExtendedMediaTrackConstraintSet = { focusMode: 'continuous' };

              try {
                await videoTrack.applyConstraints({ advanced: [focusConstraint] });
              } catch {
                // Some browsers expose continuous focus but reject applying it at runtime.
              }
            }

            const zoom = capabilities.zoom;
            if (zoom
              && Number.isFinite(zoom.min)
              && Number.isFinite(zoom.max)
              && zoom.max > zoom.min) {
              const settings = videoTrack.getSettings() as ExtendedMediaTrackSettings;
              const currentZoom = typeof settings.zoom === 'number'
                ? Math.min(zoom.max, Math.max(zoom.min, settings.zoom))
                : zoom.min;

              setCameraZoom({
                min: zoom.min,
                max: zoom.max,
                step: zoom.step && zoom.step > 0 ? zoom.step : 0.1,
                value: currentZoom,
              });
            }

            setTorchSupported(capabilities.torch === true);
            cameraSettingsRef.current = videoTrack.getSettings() as ExtendedMediaTrackSettings;
          } catch {
            setCameraZoom(null);
            setTorchSupported(false);
          }
        }
      }

      // Mode is selected once per camera session by actual QR support.
      // No UA sniffing, simultaneous decoders, periodic engine swaps or lens changes.
      const active = () => !cancelledRef.current && streamRef.current === stream && !qrClaimedRef.current;
      const beginWasm = async () => {
        try {
          // Reader-only WebAssembly and its Worker are loaded only on fallback.
          const [{ createWasmQrReader }, { captureWasmFrame }] = await Promise.all([
            import('./scannerWasmReader'),
            import('./scannerFramePolicy'),
          ]);
          if (!active()) return;
          const reader = createWasmQrReader();
          wasmReaderRef.current = reader;
          const canvas = document.createElement('canvas');
          let pass = 0;
          setCameraState('live');

          const decodeWasm = async () => {
            if (!active() || wasmReaderRef.current !== reader) return;
            try {
              const video = videoRef.current;
              if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                // Capture only a central original-resolution ROI normally; a
                // bounded full frame is sampled infrequently by the SAME engine.
                const frame = captureWasmFrame(video, canvas, pass++);
                if (frame) {
                  const value = await reader.read(frame);
                  if (!active() || wasmReaderRef.current !== reader) return;
                  if (value && looksLikeFiscalQr(value)) {
                    qrClaimedRef.current = true;
                    await acceptPayload(value);
                    return;
                  }
                }
              }
            } catch {
              // Technical WASM failure: stop processing, keep photo/manual path.
              reader.close();
              if (wasmReaderRef.current === reader) wasmReaderRef.current = null;
              if (active()) setCameraState('manual-only');
              return;
            }

            if (active() && wasmReaderRef.current === reader) {
              // Only schedule AFTER completion of this frame's decode.
              detectorTimerRef.current = window.setTimeout(() => void decodeWasm(), 250);
            }
          };

          detectorTimerRef.current = window.setTimeout(() => void decodeWasm(), 150);
        } catch {
          if (active()) setCameraState('manual-only');
        }
      };

      const Detector = getBarcodeDetector();
      let detector: BarcodeDetectorInstance | null = null;
      if (Detector) {
        try {
          const formats = typeof Detector.getSupportedFormats === 'function'
            ? await Detector.getSupportedFormats() : null;
          if (!formats || formats.includes('qr_code')) {
            detector = new Detector({ formats: ['qr_code'] });
          }
        } catch {
          // Unsupported native QR implementation: choose WASM, no toggle loop.
        }
      }
      if (!active()) return;
      if (!detector) {
        await beginWasm();
        return;
      }

      // The working Android native loop and 250/350ms schedule are preserved.
      const nativeDetector = detector;
      setCameraState('live');
      let nativeFailures = 0;
      const detect = async () => {
        if (!active() || !videoRef.current) return;
        try {
          if (videoRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            const results = await nativeDetector.detect(videoRef.current);
            if (!active()) return;
            nativeFailures = 0;
            // Tips, marketing and web links are not fiscal receipt payloads.
            const value = results.find((result) =>
              typeof result.rawValue === 'string' && looksLikeFiscalQr(result.rawValue))?.rawValue;
            if (value) {
              qrClaimedRef.current = true;
              await acceptPayload(value);
              return;
            }
          }
        } catch {
          // Native technical errors only (NOT no-QR) permit one demotion.
          nativeFailures += 1;
          if (nativeFailures >= 3 && active()) {
            await beginWasm();
            return;
          }
        }
        if (active()) {
          detectorTimerRef.current = window.setTimeout(() => void detect(), 350);
        }
      };

      detectorTimerRef.current = window.setTimeout(() => void detect(), 250);
    } catch (error) {
      if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'SecurityError')) {
        setCameraState('denied');
      } else {
        setCameraState('unavailable');
      }
    }
  }, [acceptPayload, stopCamera]);

  const onPhotoSelected = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file || photoReading) return;
    if (!file.type.startsWith('image/')) {
      setPhotoHint('Выберите фотографию чека.');
      return;
    }
    // Unknown config must never silently route an iPhone photo to legacy QR-only.
    if (previewAvailability === 'loading' || previewAvailability === 'unavailable') {
      setPhotoHint('Не удалось проверить доступность загрузки фото. Проверьте соединение и откройте сканер снова.');
      return;
    }
    stopCamera();
    if (previewAvailability === 'enabled') {
      setAlternativeMode('photo');
      setAlternativePhoto(file);
      setStage('alternatives');
      return;
    }
    setPhotoReading(true);
    setPhotoHint('Ищем QR на фотографии…');
    try {
      const { decodeQrPhoto } = await import('./qrSoftwareDecoder');
      const value = await decodeQrPhoto(file, document.createElement('canvas'));
      if (cancelledRef.current) return;
      if (value) {
        setPhotoHint(null);
        qrClaimedRef.current = true;
        await acceptPayload(value);
        return;
      }
      setPhotoHint('QR не найден. Попробуйте снимок поближе, без бликов.');
    } catch {
      if (!cancelledRef.current) setPhotoHint('Не удалось открыть фотографию. Выберите другой снимок.');
    } finally {
      setPhotoReading(false);
      if (!cancelledRef.current && !qrClaimedRef.current) void startCamera();
    }
  }, [acceptPayload, photoReading, previewAvailability, startCamera, stopCamera]);

  const beginManualEntry = useCallback(() => {
    stopCamera();
    if (previewAuthState !== 'signed_in') {
      setStage('receipt-auth-required');
      return;
    }
    setAlternativeMode('manual');
    setAlternativePhoto(null);
    setStage('alternatives');
  }, [previewAuthState, stopCamera]);

  const openScannerView = useCallback(() => {
    setOpen(true);
    setPreviewAvailability('loading');
    setPhotoHint(null);
    setAlternativePhoto(null);
    setScan(null);
    setDuplicateReceipt(null);
    setQrPayload('');
    setManualPayload('');
    setXpDelta(null);
    setPollingScanId(null);
    setMessage(null);
    setStage('camera');
    cancelledRef.current = false;
  }, []);

  const requestOpenScanner = useCallback(async () => {
    const authenticated = await readSession();

    if (authenticated) {
      try {
        const consentStatus = await fetchConsentStatus();

        if (!consentStatus.data.has_consent) {
          setOpen(true);
          setScan(null);
          setQrPayload('');
          setManualPayload('');
          setXpDelta(null);
          setPollingScanId(null);
          setMessage(null);
          setStage('consent');
          cancelledRef.current = false;
          return;
        }
      } catch {
        setOpen(true);
        setMessage('Не удалось проверить согласие с политикой конфиденциальности. Попробуйте ещё раз.');
        setStage('error');
        return;
      }
    }

    openScannerView();
  }, [openScannerView]);

  const startYandexLogin = useCallback(() => {
    const pending = normalizeQrPayload(qrPayload || manualPayload);

    if (looksLikeFiscalQr(pending)) {
      sessionStorage.setItem(PENDING_SCAN_KEY, pending);
    }

    window.location.assign('/api/v1/auth/yandex/redirect');
  }, [manualPayload, qrPayload]);

  const handleConsentGranted = useCallback(() => {
    setMessage(null);
    const pending = sessionStorage.getItem(PENDING_SCAN_KEY);

    if (pending && looksLikeFiscalQr(pending)) {
      void submitPayload(pending).catch(() => {
        setMessage('Согласие сохранено, но чек не удалось продолжить автоматически. Попробуйте ещё раз.');
        setStage('error');
      });
      return;
    }

    setStage('camera');
  }, [submitPayload]);

  const handleConsentCancel = useCallback(() => {
    cancelledRef.current = true;
    stopCamera();
    sessionStorage.removeItem(PENDING_SCAN_KEY);
    sessionStorage.setItem(AUTH_RETURN_TARGET_KEY, 'profile');
    sessionStorage.setItem(PROFILE_FLASH_KEY, 'Сканирование недоступно без согласия');
    setOpen(false);
    window.location.assign('/?auth_error=consent_cancelled');
  }, [stopCamera]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1119px)');
    const handleLayoutChange = (event: MediaQueryListEvent) => setMobileLayout(event.matches);

    setMobileLayout(media.matches);
    media.addEventListener('change', handleLayoutChange);

    return () => media.removeEventListener('change', handleLayoutChange);
  }, []);

  useEffect(() => {
    const handleOpenScanner = () => void requestOpenScanner();
    window.addEventListener('hopper:open-scanner', handleOpenScanner);

    return () => window.removeEventListener('hopper:open-scanner', handleOpenScanner);
  }, [requestOpenScanner]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const auth = url.searchParams.get('auth');
    const authError = url.searchParams.get('auth_error');

    if (!auth && !authError) {
      return;
    }

    const pending = sessionStorage.getItem(PENDING_SCAN_KEY);
    url.searchParams.delete('auth');
    url.searchParams.delete('auth_error');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);

    if (!pending || !looksLikeFiscalQr(pending)) {
      return;
    }

    cancelledRef.current = false;
    setOpen(true);
    setQrPayload(pending);

    if (auth === 'yandex') {
      void submitPayload(pending).catch(() => {
        setMessage('Вход выполнен, но чек не удалось продолжить автоматически. Попробуйте проверить его ещё раз.');
        setStage('error');
      });
      return;
    }

    setMessage(authErrorMessage(authError));
    setStage('auth-required');
  }, [submitPayload]);

  // Feature flag stays authoritative: only an EXPLICIT false enables the
  // legacy local photo path. Missing/failed config must not silently downgrade.
  useEffect(() => {
    if (!open) return;
    let stale = false;
    setPreviewAvailability('loading');
    void fetch('/api/v1/frontend-config', {
      credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json' },
    }).then(async (response) => {
      if (!response.ok) {
        if (!stale) setPreviewAvailability('unavailable');
        return;
      }
      const body = await response.json() as { frontend?: { receipt_previews?: { enabled?: boolean } } };
      const enabled = body.frontend?.receipt_previews?.enabled;
      if (!stale) setPreviewAvailability(typeof enabled === 'boolean'
        ? (enabled ? 'enabled' : 'disabled') : 'unavailable');
    }).catch(() => {
      if (!stale) setPreviewAvailability('unavailable');
    });
    return () => { stale = true; };
  }, [open]);

  // Preflight session *before* allowing the photo file picker or manual form.
  // Keep fileInput.click() synchronous with a real user gesture for iOS Safari.
  useEffect(() => {
    if (!open || previewAvailability !== 'enabled') return;
    let current = true;
    setPreviewAuthState('checking');
    void readSession().then((loggedIn) => {
      if (current) setPreviewAuthState(loggedIn ? 'signed_in' : 'signed_out');
    }).catch(() => {
      if (current) setPreviewAuthState('unavailable');
    });
    return () => { current = false; };
  }, [open, previewAvailability]);

  useEffect(() => {
    if (!open || stage !== 'camera') {
      return;
    }

    void startCamera();

    return () => {
      stopCamera();
    };
  }, [open, stage, startCamera, stopCamera]);

  // iOS PWA can suspend the video track when the app is backgrounded.
  useEffect(() => {
    if (!open || stage !== 'camera') return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopCamera();
      } else if (document.visibilityState === 'visible' && streamRef.current === null) {
        void startCamera();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [open, stage, startCamera, stopCamera]);

  useEffect(() => () => {
    cancelledRef.current = true;
    stopCamera();
  }, [stopCamera]);

  const confirmCandidate = useCallback(async (candidateId: string) => {
    if (!scan) {
      return;
    }

    try {
      setStage('processing');
      const csrf = await readCsrfToken();
      const response = await fetchBounded(`/api/v1/receipt-scans/${scan.id}/venue-confirmation`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf,
        },
        body: JSON.stringify({ candidate_id: candidateId }),
      });

      if (response.status === 401) {
        if (qrPayload) {
          sessionStorage.setItem(PENDING_SCAN_KEY, qrPayload);
        }
        setStage('auth-required');
        return;
      }

      if (!response.ok) {
        throw new Error(`Venue confirmation failed: ${response.status}`);
      }

      await pollScan(scan.id);
    } catch {
      setMessage('Не удалось подтвердить место. Попробуйте ещё раз.');
      setStage('error');
    }
  }, [pollScan, qrPayload, scan]);

  const resetScanner = useCallback(() => {
    sessionStorage.removeItem(PENDING_SCAN_KEY);
    setScan(null);
    setDuplicateReceipt(null);
    setQrPayload('');
    setManualPayload('');
    setAlternativePhoto(null);
    setXpDelta(null);
    setPollingScanId(null);
    setMessage(null);
    setStage('camera');
  }, []);

  const preview = parseFiscalPreview(qrPayload || manualPayload);
  const receipt = scan?.receipt ?? null;
  const venueName = receipt?.venue?.name ?? null;
  const duplicateAmount = receipt?.total_amount ?? duplicateReceipt?.total_amount ?? preview.amount;
  const duplicateCurrency = receipt?.currency ?? duplicateReceipt?.currency ?? 'RUB';
  const duplicateVenue = venueName ?? duplicateReceipt?.merchant_name ?? null;
  const venueId = receipt?.venue?.id ?? null;
  const candidates = scan?.venue_resolution?.candidates?.filter((candidate) => candidate.status !== 'rejected') ?? [];
  const offerActivated = scanActivatedOffer(scan);

  const openVenueImpressions = () => {
    if (!venueId) {
      return;
    }

    navigateAndFocus(
      `/venue/${encodeURIComponent(venueId)}#venue-impressions-title`,
      'venue-impressions-title',
    );
  };

  const openXpHistory = () => {
    navigateAndFocus('/profile#profile-recent-visits', 'profile-recent-visits');
  };

  return (
    <>
      {!mobileLayout && (
        <button className="scanner-launcher" type="button" onClick={() => void requestOpenScanner()} aria-label="Сканировать чек">
          <Icon name="scan" />
          <span>Скан</span>
        </button>
      )}

      {open && (
        <div className="scanner-overlay" role="dialog" aria-modal="true" aria-label="Сканирование фискального чека">
          <div className="scanner-shell">
            <header className="scanner-topbar">
              <button className="scanner-close" type="button" onClick={closeScanner} aria-label="Закрыть сканер">
                <HopperIcon name="close" size={20} />
              </button>
              <div>
                <strong>Сканировать чек</strong>
                <span>Подтверждаем реальные визиты</span>
              </div>
              <span className="scanner-topbar__spacer" />
            </header>

            {stage === 'consent' && (
              <ConsentScreen onConsented={handleConsentGranted} onCancel={handleConsentCancel} />
            )}

            {stage === 'camera' && (
              <div className={`scanner-camera-stage scanner-camera-stage--${cameraState}`}>
                <div className="scanner-camera">
                  <video ref={videoRef} muted playsInline />
                  <div className="scanner-camera__shade" />
                  <div className="scanner-qr-frame">
                    <span /><span /><span /><span />
                  </div>
                  <div className="scanner-camera-status">
                    {cameraState === 'starting' && 'Включаем камеру…'}
                    {cameraState === 'live' && 'Ищем QR-код чека'}
                    {cameraState === 'manual-only' && 'Камера включена · автоматическое распознавание QR недоступно'}
                    {cameraState === 'denied' && 'Нет доступа к камере'}
                    {cameraState === 'unavailable' && 'Камера недоступна'}
                  </div>
                </div>

                {cameraState === 'live' && (cameraZoom !== null || torchSupported) && (
                  <div className="scanner-camera-actions" aria-label="Настройки камеры">
                    {cameraZoom !== null && (
                      <label className="scanner-zoom-control">
                        <span>Масштаб {cameraZoom.value.toFixed(1)}×</span>
                        <input
                          type="range"
                          min={cameraZoom.min}
                          max={cameraZoom.max}
                          step={cameraZoom.step}
                          value={cameraZoom.value}
                          onChange={(event) => void applyZoom(Number(event.target.value))}
                          aria-label="Масштаб камеры"
                        />
                      </label>
                    )}
                    {torchSupported && (
                      <button
                        className="scanner-camera-control-button"
                        type="button"
                        onClick={() => void toggleTorch()}
                        aria-pressed={torchEnabled}
                        aria-label={torchEnabled ? 'Выключить фонарик' : 'Включить фонарик'}
                      >
                        <HopperIcon name={torchEnabled ? 'flash-off' : 'flash'} size={20} />
                        <span>{torchEnabled ? 'Выключить свет' : 'Включить свет'}</span>
                      </button>
                    )}
                  </div>
                )}

                <div className="scanner-ios-fallback">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => void onPhotoSelected(event)}
                  />
                  <button
                    type="button"
                    className="scanner-camera-control-button"
                    disabled={photoReading || previewAvailability === 'loading' || previewAvailability === 'unavailable'
                      || (previewAvailability === 'enabled' && ['checking', 'unavailable'].includes(previewAuthState))}
                    onClick={() => {
                      if (previewAvailability === 'enabled' && previewAuthState !== 'signed_in') {
                        stopCamera();
                        setStage('receipt-auth-required');
                        return;
                      }
                      photoInputRef.current?.click();
                    }}
                  >
                    {photoReading ? 'Читаем фотографию…' : previewAvailability === 'loading' ? 'Проверяем доступность фото…' : previewAvailability === 'unavailable' ? 'Загрузка фото временно недоступна' : previewAvailability === 'enabled' && previewAuthState === 'signed_out'
                      ? 'Войти, чтобы загрузить фото' : 'Выбрать фото чека'}
                  </button>
                  {photoHint && <p role="status">{photoHint}</p>}
                  {previewAvailability === 'unavailable' && <p role="status">Загрузка фото временно недоступна. Проверьте соединение и откройте сканер снова.</p>}
                  {previewAvailability === 'enabled' && previewAuthState === 'signed_out' && (
                    <p role="status">Сначала войдите в аккаунт Хоппера — после входа можно загрузить фото или ввести реквизиты.</p>
                  )}
                  {previewAvailability === 'enabled' && previewAuthState === 'unavailable' && (
                    <p role="status">Не удалось проверить вход. Проверьте соединение и откройте сканер снова.</p>
                  )}
                  {previewAvailability === 'enabled' && (
                    <button type="button" className="scanner-alternative-manual-link"
                      onClick={beginManualEntry}>Ввести данные вручную</button>
                  )}
                </div>

                <div className="scanner-instructions">
                  <Icon name="scan" />
                  <div>
                    <h2>Наведите камеру на QR-код</h2>
                    {cameraState === 'live'
                      ? <p>Приблизьте камеру к QR-коду на чеке и обеспечьте хорошее освещение.</p>
                      : <p>Он находится внизу фискального чека. Хоппер использует его, чтобы подтвердить реальный визит.</p>}
                  </div>
                </div>

                {cameraState === 'manual-only' && previewAvailability === 'disabled' && (
                  <div className="scanner-camera-help scanner-camera-help--manual">
                    <HopperIcon name="info" size={22} />
                    <div>
                      <strong>Скопируйте строку QR вручную</strong>
                      <span>Нужна строка из фискального QR целиком — от параметра t= до n=1.</span>
                      <code className="scanner-manual-example">t=20231018T1430&amp;s=1500.00&amp;fn=...&amp;i=...&amp;fp=...&amp;n=1</code>
                    </div>
                  </div>
                )}

                {cameraState === 'denied' && (
                  <div className="scanner-camera-help scanner-camera-help--denied">
                    <HopperIcon name="camera" size={22} />
                    <div>
                      <strong>Разрешите доступ к камере</strong>
                      <span>В настройках сайта или браузера разрешите камеру для Хоппера, затем попробуйте снова.</span>
                    </div>
                  </div>
                )}

                {(cameraState === 'denied' || cameraState === 'unavailable') && (
                  <button className="scanner-outline-button" type="button" onClick={() => void startCamera()}>
                    Попробовать камеру снова
                  </button>
                )}

                {previewAvailability === 'disabled' && <details className="scanner-manual" open={cameraState === 'manual-only' ? true : undefined}>
                  <summary>Не получается отсканировать?</summary>
                  <label>
                    <span>Вставьте строку из QR фискального чека</span>
                    <textarea
                      value={manualPayload}
                      onChange={(event) => setManualPayload(event.target.value)}
                      placeholder="t=...&s=...&fn=...&i=...&fp=...&n=1"
                      rows={3}
                    />
                  </label>
                  <button
                    className="scanner-primary-button"
                    type="button"
                    disabled={!manualPayload.trim()}
                    onClick={() => void acceptPayload(manualPayload)}
                  >
                    Проверить чек
                  </button>
                </details>}
              </div>
            )}

            {stage === 'alternatives' && (
              <ReceiptAlternativeFlow
                initialMode={alternativeMode}
                initialPhoto={alternativePhoto}
                onBack={() => {
                  setAlternativePhoto(null);
                  setStage('camera');
                }}
                onScanCreated={(id) => { void pollScan(id); }}
                onReceiptAlreadyUsed={(details) => {
                  setScan(null);
                  setQrPayload('');
                  setMessage(null);
                  setXpDelta(null);
                  setPollingScanId(null);
                  setDuplicateReceipt(details ?? null);
                  setStage('already-submitted');
                }}
              />
            )}

            {stage === 'receipt-auth-required' && (
              <div className="scanner-sheet scanner-sheet--centered">
                <div className="scanner-state-icon"><Icon name="profile" /></div>
                <span className="scanner-kicker">Нужен вход</span>
                <h2>Сначала войдите в Хоппер</h2>
                <p>Чтобы загрузить фото чека или ввести его реквизиты, войдите через Яндекс. Затем откройте сканер и выберите нужный способ.</p>
                <button className="scanner-primary-button" type="button" onClick={startYandexLogin}>Войти через Яндекс</button>
                <button className="scanner-text-button" type="button" onClick={resetScanner}>Вернуться к сканеру</button>
              </div>
            )}

            {stage === 'auth-required' && (
              <div className="scanner-sheet scanner-sheet--centered">
                <div className="scanner-state-icon"><Icon name="profile" /></div>
                <span className="scanner-kicker">QR считан</span>
                <h2>Войдите, чтобы подтвердить визит</h2>
                <p>{message ?? 'Чек остаётся у вас в браузере. После входа Хоппер сможет проверить его, создать визит и начислить XP.'}</p>
                <div className="scanner-receipt-preview">
                  <div><span>Сумма</span><strong>{formatMoney(preview.amount)}</strong></div>
                  <div><span>Дата</span><strong>{preview.date ?? 'Из QR чека'}</strong></div>
                </div>
                <button className="scanner-primary-button" type="button" onClick={startYandexLogin}>Войти через Яндекс</button>
                <button className="scanner-text-button" type="button" onClick={resetScanner}>Сканировать другой чек</button>
              </div>
            )}

            {(stage === 'submitting' || stage === 'processing') && (
              <div className="scanner-sheet scanner-sheet--centered">
                <div className="scanner-progress-ring"><span /></div>
                <span className="scanner-kicker">Чек найден</span>
                <h2>{stage === 'submitting' ? 'Отправляем чек' : processingLabel(scan)}</h2>
                <p>Проверяем чек и визит. Оставьте этот экран открытым до результата.</p>
                {receipt && (
                  <div className="scanner-receipt-preview">
                    <div><span>Сумма</span><strong>{formatMoney(receipt.total_amount, receipt.currency)}</strong></div>
                    <div><span>Место</span><strong>{venueName ?? 'Определяем…'}</strong></div>
                  </div>
                )}
              </div>
            )}

            {stage === 'processing-delayed' && (
              <div className="scanner-sheet scanner-sheet--centered scanner-polling-error">
                <div className="scanner-state-icon"><HopperIcon name="info" size={36} /></div>
                <span className="scanner-kicker">Чек принят</span>
                <h2>Обработка чека задерживается</h2>
                <p>Чек сохранён. Вы можете проверить результат позже. Повторно отправлять его не нужно.</p>
                <button className="scanner-primary-button" type="button" onClick={() => void retryPollingStatus()}>
                  Проверить статус
                </button>
                <button className="scanner-text-button" type="button" onClick={closeScanner}>Закрыть</button>
              </div>
            )}

            {stage === 'polling-error' && (
              <div className="scanner-sheet scanner-sheet--centered scanner-polling-error">
                <div className="scanner-error-icon"><HopperIcon name="offline" size={36} /></div>
                <span className="scanner-kicker">Связь прервалась</span>
                <h2>Статус чека ещё можно проверить</h2>
                <p>{message ?? 'Не удалось получить свежий статус. Сам чек не нужно отправлять заново.'}</p>
                <button className="scanner-primary-button" type="button" onClick={() => void retryPollingStatus()}>
                  Проверить статус вручную
                </button>
                <button className="scanner-text-button" type="button" onClick={closeScanner}>Закрыть</button>
              </div>
            )}

            {stage === 'already-submitted' && (
              <div className="scanner-sheet scanner-sheet--centered scanner-already-submitted">
                <div className="scanner-state-icon"><HopperIcon name="check-circle" size={34} /></div>
                <span className="scanner-kicker">Повторный чек</span>
                <h2>Чек не удалось засчитать</h2>
                <p>{RECEIPT_ALREADY_USED_TEXT}</p>
                {(duplicateAmount || duplicateVenue) && (
                  <div className={duplicateAmount && duplicateVenue ? 'scanner-receipt-preview' : 'scanner-receipt-preview scanner-receipt-preview--single'}>
                    {duplicateAmount && <div><span>Чек</span><strong>{formatMoney(duplicateAmount, duplicateCurrency)}</strong></div>}
                    {duplicateVenue && <div><span>Место</span><strong>{duplicateVenue}</strong></div>}
                  </div>
                )}
                <button className="scanner-primary-button" type="button" onClick={closeScanner}>Вернуться в Хоппер</button>
                {scan?.outcome === 'already_submitted' && (
                  <button className="scanner-secondary-button" type="button" onClick={openXpHistory}>
                    <HopperIcon name="profile" size={18} />
                    Открыть историю визитов
                  </button>
                )}
                <button className="scanner-text-button" type="button" onClick={resetScanner}>Сканировать другой чек</button>
              </div>
            )}

            {stage === 'candidate' && (
              <div className="scanner-sheet">
                <span className="scanner-kicker">Нужно уточнение</span>
                <h2>Это то место?</h2>
                <p>Мы нашли заведение по данным фискального чека. Подтвердите, где вы были.</p>
                <div className="scanner-candidates">
                  {candidates.map((candidate) => (
                    <button key={candidate.id} type="button" onClick={() => void confirmCandidate(candidate.id)}>
                      <span className="scanner-candidate-pin"><Icon name="location" /></span>
                      <span>
                        <strong>{candidate.name ?? 'Место без названия'}</strong>
                        <small>{candidate.address ?? 'Адрес уточняется'}</small>
                      </span>
                      <span className="scanner-candidate-action">Это оно</span>
                    </button>
                  ))}
                </div>
                <button className="scanner-text-button" type="button" onClick={closeScanner}>Не вижу своего места</button>
              </div>
            )}

            {(stage === 'success' || stage === 'historical') && (
              <div className="scanner-sheet scanner-sheet--success scanner-sheet--centered">
                <div className="scanner-success-icon"><HopperIcon name="check-circle" size={40} /></div>
                <span className="scanner-kicker">{stage === 'historical' ? 'История подтверждена' : 'Готово'}</span>
                <h2>{stage === 'historical' ? 'Визит добавлен в историю' : 'Визит засчитан!'}</h2>
                <p>{venueName ?? 'Заведение подтверждено'}{preview.date ? ` · ${preview.date}` : ''}</p>
                <div className="scanner-success-card">
                  <div><span>Чек</span><strong>{formatMoney(receipt?.total_amount ?? preview.amount, receipt?.currency)}</strong></div>
                  <div>
                    <span>XP</span>
                    <strong>{xpDelta !== null ? (xpDelta > 0 ? `+${xpDelta} XP` : 'Без начисления') : 'Смотреть в профиле'}</strong>
                  </div>
                </div>

                {offerActivated && (
                  <div className="scanner-offer-activation">
                    <HopperIcon name="gift" size={18} />
                    <span>Предложение активировано</span>
                  </div>
                )}

                <button className="scanner-primary-button" type="button" onClick={closeScanner}>Вернуться в Хоппер</button>
                <div className="scanner-post-actions" aria-label="Действия после визита">
                  <button
                    className="scanner-secondary-button"
                    type="button"
                    disabled={!venueId}
                    onClick={openVenueImpressions}
                  >
                    <HopperIcon name="message" size={18} />
                    Оставить впечатление о месте
                  </button>
                  <button className="scanner-secondary-button" type="button" onClick={openXpHistory}>
                    <HopperIcon name="xp" size={18} />
                    Посмотреть начисленный XP
                  </button>
                </div>
                <button className="scanner-text-button" type="button" onClick={resetScanner}>Сканировать ещё</button>
              </div>
            )}

            {stage === 'merchant-review' && (
              <div className="scanner-sheet scanner-sheet--centered">
                <div className="scanner-state-icon"><HopperIcon name="info" size={36} /></div>
                <span className="scanner-kicker">Чек сохранён</span>
                <h2>Требуется дополнительная проверка</h2>
                <p>Не удалось однозначно определить тип бизнеса. Чек сохранён для дополнительной проверки. Визит и XP пока не начислены.</p>
                <button className="scanner-primary-button" type="button" onClick={closeScanner}>Вернуться в Хоппер</button>
              </div>
            )}

            {stage === 'merchant-ineligible' && (
              <div className="scanner-sheet scanner-sheet--centered">
                <div className="scanner-error-icon"><HopperIcon name="error" size={36} /></div>
                <span className="scanner-kicker">Чек проверен</span>
                <h2>Это не заведение HoReCa</h2>
                <p>Хоппер не засчитывает визиты по чекам из магазинов и других неподдерживаемых типов бизнеса. Визит и XP не созданы.</p>
                <button className="scanner-primary-button" type="button" onClick={closeScanner}>Вернуться в Хоппер</button>
                <button className="scanner-text-button" type="button" onClick={resetScanner}>Сканировать другой чек</button>
              </div>
            )}

            {stage === 'review' && (
              <div className="scanner-sheet scanner-sheet--centered">
                <div className="scanner-state-icon"><Icon name="sparkle" /></div>
                <span className="scanner-kicker">Чек принят</span>
                <h2>Нужна дополнительная проверка</h2>
                <p>Мы сохранили чек. После дополнительной проверки результат будет виден в профиле Хоппера.</p>
                <button className="scanner-primary-button" type="button" onClick={closeScanner}>Понятно</button>
              </div>
            )}

            {(stage === 'rejected' || stage === 'error') && (
              <div className="scanner-sheet scanner-sheet--centered">
                {stage === 'rejected' ? (
                  <ReceiptRejectionNotice message={message ?? 'Попробуйте ещё раз. Если ошибка повторится, чек можно проверить позднее.'} />
                ) : (
                  <>
                    <div className="scanner-error-icon"><HopperIcon name="error" size={36} /></div>
                    <span className="scanner-kicker">Не получилось</span>
                    <h2>Что-то пошло не так</h2>
                    <p>{message ?? 'Попробуйте ещё раз. Если ошибка повторится, чек можно проверить позднее.'}</p>
                  </>
                )}
                <button className="scanner-primary-button" type="button" onClick={resetScanner}>Попробовать снова</button>
                <button className="scanner-text-button" type="button" onClick={closeScanner}>Закрыть</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
